import { BaseApiClient } from "./base-api"

const API_BASE_URL = import.meta.env.VITE_BASE_URL

// Parsnip-specific types
export type AnalysisStatus = 'pending' | 'completed' | 'failed'

// Parsnip-specific interfaces
export interface ParsnipVideo {
  id: string
  video_category: 'content' | 'result'
  file_name: string
  video_url: string | null
  thumbnail_url: string | null
  duration: number | null
  created_at: string
}

export interface ParsnipAnalysis {
  id: string
  pet_id: string | null
  pet_name: string | null
  bullet_points: string[]
  summary: string
  content_reactions: string[]
  content_to_remove: string[]
  content_suggestions: string[]
  created_at: string
}

export interface ParsnipResult {
  analysis: ParsnipAnalysis
  videos: ParsnipVideo[]
}

export interface UploadVideoResponse {
  success: boolean
  video: {
    id: string
    video_category: string
    file_name: string
    video_url: string
    created_at: string
  }
}

export interface AnalyzeRequest {
  analysis_id?: string  // If provided, re-run analysis on existing record
  pet_id?: string
  pet_name?: string
  content_video_id: string
  result_video_ids: string[]
}

export interface AnalyzeResponse {
  success: boolean
  analysis_id: string
  // Populated immediately when running synchronously (local dev)
  status: AnalysisStatus
  bullet_points?: string[]
  summary?: string
  content_reactions?: string[]
  content_to_remove?: string[]
  content_suggestions?: string[]
}

export interface AnalysisStatusResponse {
  status: AnalysisStatus
  analysis_id?: string
  bullet_points?: string[]
  summary?: string
  content_reactions?: string[]
  content_to_remove?: string[]
  content_suggestions?: string[]
  error?: string
}

export interface MyAnalysesResponse {
  success: boolean
  analyses: Array<{
    id: string
    user_email?: string  // Only returned in all-analyses endpoint (admin)
    pet_id: string | null
    pet_name: string | null
    summary: string
    bullet_points: string[]
    content_reactions: string[]
    content_to_remove: string[]
    content_suggestions: string[]
    status: AnalysisStatus
    video_count: number
    content_video_url: string | null
    content_thumbnail_url: string | null
    content_video_id: string | null
    result_video_urls: string[]
    result_thumbnail_urls: (string | null)[]
    result_video_ids: string[]
    created_at: string
  }>
  total: number
  has_more?: boolean
  skip?: number
  limit?: number
}

export interface SignedUploadUrlResponse {
  success: boolean
  signed_url: string
  blob_name: string
  public_url: string
  expires_in_minutes: number
  batch_id: string  // Batch ID for grouping videos in one analysis
  upload_metadata: {
    file_name: string
    file_size: number
    video_category: string
  }
}

export interface UploadProgress {
  percent: number
  bytesUploaded: number
  totalBytes: number
  speed: number // bytes per second
  eta: number // seconds remaining
  mbps: number // megabits per second
}

export interface ConfirmUploadResponse {
  success: boolean
  video: {
    id: string
    video_category: string
    file_name: string
    video_url: string
    file_size: number
    created_at: string
  }
}

/**
 * Get a signed URL for direct upload to GCS (efficient for large files)
 */
export const getUploadUrl = async (
  fileName: string,
  fileSize: number,
  contentType: string,
  videoCategory: 'content' | 'result',
  batchId?: string  // Optional: pass existing batch_id to group videos
): Promise<SignedUploadUrlResponse> => {
  const formData = new FormData()
  formData.append('file_name', fileName)
  formData.append('file_size', fileSize.toString())
  formData.append('content_type', contentType)
  formData.append('video_category', videoCategory)
  if (batchId) {
    formData.append('batch_id', batchId)
  }

  const { Authorization } = await BaseApiClient.getAuthHeaders()
  const response = await fetch(`${API_BASE_URL}/picasso/parsnip/get-upload-url`, {
    method: 'POST',
    headers: { Authorization },
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get upload URL' }))
    throw new Error(error.detail || 'Failed to get upload URL')
  }

  return response.json()
}

/**
 * Upload file directly to GCS using signed URL
 */
export const uploadToGCS = async (
  signedUrl: string,
  file: File,
  contentType: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Set timeout to 2 hours (for very large files on slow connections)
    xhr.timeout = 2 * 60 * 60 * 1000

    // Track upload progress with speed estimation
    const startTime = Date.now()
    let lastLoaded = 0
    let lastTime = startTime
    let speedSamples: number[] = [] // Rolling average for smoother speed display

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const now = Date.now()
        const elapsed = (now - lastTime) / 1000 // seconds
        
        if (elapsed > 0.5) { // Update every 500ms
          // Calculate instantaneous speed
          const bytesPerSecond = (e.loaded - lastLoaded) / elapsed
          
          // Keep last 5 samples for rolling average
          speedSamples.push(bytesPerSecond)
          if (speedSamples.length > 5) {
            speedSamples.shift()
          }
          
          // Use average speed for smoother ETA
          const avgSpeed = speedSamples.reduce((a, b) => a + b, 0) / speedSamples.length
          const remainingBytes = e.total - e.loaded
          const eta = avgSpeed > 0 ? remainingBytes / avgSpeed : 0
          const mbps = (avgSpeed * 8) / (1024 * 1024)
          
          const percent = Math.round((e.loaded / e.total) * 100)
          
          onProgress({
            percent,
            bytesUploaded: e.loaded,
            totalBytes: e.total,
            speed: avgSpeed,
            eta: Math.round(eta),
            mbps: Number.parseFloat(mbps.toFixed(2)),
          })
          
          lastLoaded = e.loaded
          lastTime = now
        }
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        // Send final 100% progress update before resolving
        if (onProgress) {
          onProgress({
            percent: 100,
            bytesUploaded: file.size,
            totalBytes: file.size,
            speed: 0,
            eta: 0,
            mbps: 0,
          })
        }
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'))
    })

    xhr.addEventListener('timeout', () => {
      reject(new Error('Upload timed out (2 hour limit)'))
    })

    xhr.open('PUT', signedUrl)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.send(file)
  })
}

/**
 * Confirm upload and create database record
 */
export const confirmUpload = async (
  blobName: string,
  fileName: string,
  fileSize: number,
  videoCategory: 'content' | 'result'
): Promise<ConfirmUploadResponse> => {
  const formData = new FormData()
  formData.append('blob_name', blobName)
  formData.append('file_name', fileName)
  formData.append('file_size', fileSize.toString())
  formData.append('video_category', videoCategory)

  const { Authorization } = await BaseApiClient.getAuthHeaders()
  const response = await fetch(`${API_BASE_URL}/picasso/parsnip/confirm-upload`, {
    method: 'POST',
    headers: { Authorization },
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to confirm upload' }))
    throw new Error(error.detail || 'Failed to confirm upload')
  }

  return response.json()
}

/**
 * Upload a video using signed URL (efficient for large files up to 1GB+)
 * This is the recommended method for all uploads.
 */
export const uploadVideoWithSignedUrl = async (
  file: File,
  videoCategory: 'content' | 'result',
  onProgress?: (progress: UploadProgress) => void,
  batchId?: string  // Optional: pass to group videos in same session
): Promise<UploadVideoResponse & { batch_id: string }> => {
  // Step 1: Get signed URL (with optional batch_id)
  const uploadUrlResponse = await getUploadUrl(
    file.name,
    file.size,
    file.type || 'video/mp4',
    videoCategory,
    batchId
  )

  // Step 2: Upload directly to GCS with progress tracking
  await uploadToGCS(
    uploadUrlResponse.signed_url,
    file,
    file.type || 'video/mp4',
    onProgress
  )

  // Step 3: Confirm upload and create DB record
  const confirmResponse = await confirmUpload(
    uploadUrlResponse.blob_name,
    file.name,
    file.size,
    videoCategory
  )

  // Return response with batch_id
  return {
    ...confirmResponse,
    batch_id: uploadUrlResponse.batch_id
  }
}

/**
 * Analyze pet reactions using Gemini
 */
export const analyzePetReactions = async (
  request: AnalyzeRequest
): Promise<AnalyzeResponse> => {
  return BaseApiClient.postRequest<AnalyzeResponse>(
    '/picasso/parsnip/analyze',
    request,
    'analyze pet reactions'
  )
}

/**
 * Poll the status of an async analysis job.
 */
export const getAnalysisStatus = async (
  analysisId: string
): Promise<AnalysisStatusResponse> => {
  return BaseApiClient.getRequest<AnalysisStatusResponse>(
    `/picasso/parsnip/analysis/${analysisId}/status`,
    'get analysis status'
  )
}

/**
 * Get analysis result with all videos
 */
export const getAnalysis = async (
  analysisId: string
): Promise<ParsnipResult> => {
  return BaseApiClient.getRequest<ParsnipResult>(
    `/picasso/parsnip/analysis/${analysisId}`,
    'get analysis'
  )
}

/**
 * Get all analyses for the current user
 */
export const getMyAnalyses = async (
  skip: number = 0,
  limit: number = 20,
  sortBy: string = 'newest',
  status?: string
): Promise<MyAnalysesResponse> => {
  const params: Record<string, string | number | boolean | undefined> = { skip, limit, sort_by: sortBy }
  if (status && status !== 'all') params.status = status
  return BaseApiClient.getRequest<MyAnalysesResponse>(
    '/picasso/parsnip/my-analyses',
    'get my analyses',
    params
  )
}

/**
 * Get all analyses from all users (admin only)
 */
export const getAllAnalyses = async (
  skip: number = 0,
  limit: number = 100,
  sortBy: string = 'newest',
  status?: string,
  userEmail?: string
): Promise<MyAnalysesResponse> => {
  const params: Record<string, string | number | boolean | undefined> = { skip, limit, sort_by: sortBy }
  if (status && status !== 'all') params.status = status
  if (userEmail) params.user_email = userEmail
  return BaseApiClient.getRequest<MyAnalysesResponse>(
    '/picasso/parsnip/all-analyses',
    'get all analyses',
    params
  )
}

/**
 * Delete an analysis and all associated GCS video files
 */
export const deleteAnalysis = async (analysisId: string): Promise<{ success: boolean }> => {
  return BaseApiClient.deleteRequest<{ success: boolean }>(
    `/picasso/parsnip/analysis/${analysisId}`,
    'delete analysis'
  )
}

/**
 * Get distinct user emails that have analyses (admin only)
 */
export const getAnalysisUsers = async (): Promise<{ success: boolean; users: string[] }> => {
  return BaseApiClient.getRequest<{ success: boolean; users: string[] }>(
    '/picasso/parsnip/analysis-users',
    'get analysis users'
  )
}

// Export as a single object for backward compatibility
export const parsnipApi = {
  uploadVideoWithSignedUrl,
  getUploadUrl,
  uploadToGCS,
  confirmUpload,
  analyzePetReactions,
  getAnalysisStatus,
  getAnalysis,
  getMyAnalyses,
  getAllAnalyses,
  getAnalysisUsers,
  deleteAnalysis,
}
